import { getAxiosInstance, isAxiosError } from '@seeqdev/api';
import type {
  CreateSkillRequest,
  DraftStateResponse,
  ExtraFiles,
  FileSource,
  GenerateSkillRequest,
  GenerateSkillResponse,
  PublishVersionRequest,
  PublishVersionResponse,
  SaveDraftVersionRequest,
  SaveDraftVersionResponse,
  Skill,
  SkillChatResponse,
  SkillDraftResponse,
  SkillDraftSummary,
  SkillSummary,
  TestSkillRequest,
  TestSkillResponse,
  UpdateSkillRequest,
  VersionContentResponse,
  VersionListResponse,
} from '@/skillsBuilder/skillsBuilder.types';

const SKILLS_API_URL = '/genai/skills';

// ============================================================================
// Core CRUD Operations
// ============================================================================

export const listSkills = async (): Promise<SkillSummary[]> => {
  const { data } = await getAxiosInstance().get<SkillSummary[]>(SKILLS_API_URL);
  return data;
};

export const getSkill = async (skillId: string): Promise<Skill> => {
  const { data } = await getAxiosInstance().get<Skill>(`${SKILLS_API_URL}/${skillId}`);
  return data;
};

export const createSkill = async (request: CreateSkillRequest): Promise<Skill> => {
  const { data } = await getAxiosInstance().post<Skill>(SKILLS_API_URL, request);
  return data;
};

export const updateSkill = async (skillId: string, request: UpdateSkillRequest): Promise<Skill> => {
  const { data } = await getAxiosInstance().put<Skill>(`${SKILLS_API_URL}/${skillId}`, request);
  return data;
};

export const deleteSkill = async (skillId: string): Promise<void> => {
  await getAxiosInstance().delete(`${SKILLS_API_URL}/${skillId}`);
};

// ============================================================================
// Skill Drafts
// ============================================================================

export const listSkillDrafts = async (): Promise<SkillDraftSummary[]> => {
  const { data } = await getAxiosInstance().get<SkillDraftSummary[]>(`${SKILLS_API_URL}/drafts`);
  return data;
};

export const getSkillDraft = async (chatId: string): Promise<SkillDraftResponse | null> => {
  try {
    const { data } = await getAxiosInstance().get<SkillDraftResponse>(`${SKILLS_API_URL}/draft/${chatId}`);
    return data;
  } catch (error) {
    if (isAxiosError(error) && error.response?.status === 404) {
      return null;
    }
    throw error;
  }
};

export const updateSkillDraftApi = async (
  chatId: string,
  skillMd: string,
  scripts: Record<string, string>,
  requirements: string[],
  scriptSources: Record<string, FileSource>,
  extraFiles?: ExtraFiles,
): Promise<SkillDraftResponse> => {
  const { data } = await getAxiosInstance().put<SkillDraftResponse>(`${SKILLS_API_URL}/draft/${chatId}`, {
    skill_md: skillMd,
    scripts,
    requirements,
    script_sources: scriptSources,
    extra_files: extraFiles,
  });
  return data;
};

export const deleteSkillDraft = async (chatId: string): Promise<void> => {
  try {
    await getAxiosInstance().delete(`${SKILLS_API_URL}/draft/${chatId}`);
  } catch (error) {
    if (isAxiosError(error) && error.response?.status === 404) {
      return; // Silently ignore 404 - draft already deleted
    }
    throw error;
  }
};

// ============================================================================
// AI-Assisted Generation
// ============================================================================

export const generateSkill = async (request: GenerateSkillRequest): Promise<GenerateSkillResponse> => {
  const { data } = await getAxiosInstance().post<GenerateSkillResponse>(`${SKILLS_API_URL}/generate`, request);
  return data;
};

// ============================================================================
// Testing
// ============================================================================

export const testSkillEndpoint = async (skillId: string, request: TestSkillRequest): Promise<TestSkillResponse> => {
  const { data } = await getAxiosInstance().post<TestSkillResponse>(`${SKILLS_API_URL}/${skillId}/test`, request);
  return data;
};

// ============================================================================
// Chat History
// ============================================================================

export const getSkillChatHistory = async (skillId: string): Promise<SkillChatResponse> => {
  const { data } = await getAxiosInstance().get<SkillChatResponse>(`${SKILLS_API_URL}/${skillId}/chat`);
  return data;
};

// ============================================================================
// Versioning
// ============================================================================

export const getSkillVersions = async (skillId: string): Promise<VersionListResponse> => {
  const { data } = await getAxiosInstance().get<VersionListResponse>(`${SKILLS_API_URL}/${skillId}/versions`);
  return data;
};

export const getSkillVersionContent = async (skillId: string, ref: string): Promise<VersionContentResponse> => {
  const { data } = await getAxiosInstance().get<VersionContentResponse>(
    `${SKILLS_API_URL}/${skillId}/versions/${encodeURIComponent(ref)}`,
  );
  return data;
};

export const publishSkillVersion = async (
  skillId: string,
  request: PublishVersionRequest,
): Promise<PublishVersionResponse> => {
  const { data } = await getAxiosInstance().post<PublishVersionResponse>(
    `${SKILLS_API_URL}/${skillId}/versions`,
    request,
  );
  return data;
};

export const saveSkillDraftVersion = async (
  skillId: string,
  request: SaveDraftVersionRequest,
): Promise<SaveDraftVersionResponse> => {
  const { data } = await getAxiosInstance().post<SaveDraftVersionResponse>(
    `${SKILLS_API_URL}/${skillId}/drafts`,
    request,
  );
  return data;
};

export const restoreSkillDraftVersion = async (skillId: string, sha: string): Promise<DraftStateResponse> => {
  const { data } = await getAxiosInstance().post<DraftStateResponse>(
    `${SKILLS_API_URL}/${skillId}/drafts/${encodeURIComponent(sha)}/restore`,
  );
  return data;
};

export const discardSkillDraftVersion = async (skillId: string): Promise<DraftStateResponse> => {
  const { data } = await getAxiosInstance().delete<DraftStateResponse>(`${SKILLS_API_URL}/${skillId}/draft`);
  return data;
};

export const revertSkillToVersion = async (skillId: string, versionLabel: string): Promise<DraftStateResponse> => {
  const { data } = await getAxiosInstance().post<DraftStateResponse>(
    `${SKILLS_API_URL}/${skillId}/revert/${encodeURIComponent(versionLabel)}`,
  );
  return data;
};
